import { motion } from "framer-motion";
import { Skull, Megaphone, Rocket } from "lucide-react";

const acts = [
  {
    icon: Skull,
    num: "01",
    act: "ACT I",
    date: "DEC 2022",
    title: "THE RUBBLE",
    accent: "#9945FF",
    text: "SOL cratered from ATHs into single digits — gutted by the FTX collapse. Twitter had already written the obituary: \u201CSolana is dead.\u201D",
    statLabel: "DRAWDOWN FROM ATH",
    statValue: "-97%",
    stamp: "\u201CSOLANA IS DEAD\u201D",
  },
  {
    icon: Megaphone,
    num: "02",
    act: "ACT II",
    date: "THE CALL",
    title: "THE BOTTOM",
    accent: "#14F195",
    text: "Curb didn't buy the eulogy. Macro cycle structure. Wave counts. A bottom nobody else wanted to call — and he put it out loud, on the record.",
    statLabel: "THE ENTRY",
    statValue: "$8",
    stamp: "POSTED. LOUD.",
  },
  {
    icon: Rocket,
    num: "03",
    act: "ACT III",
    date: "18 MO LATER",
    title: "THE VINDICATION",
    accent: "#00FFD1",
    text: "SOL trading north of $200. The bears deleted their tweets. The obituary got a correction. The bull never blinked.",
    statLabel: "SOL PRICE",
    statValue: "$200+",
    stamp: "THAT'S CURB.",
  },
];

const ActCard = ({ a, i }) => (
  <motion.article
    data-testid={`lore-card-${i + 1}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay: i * 0.15, type: "spring" }}
    className="act-card relative overflow-hidden rounded-2xl p-7 flex flex-col"
    style={{ "--act-accent": a.accent, marginTop: i === 1 ? "2.5rem" : 0 }}
  >
    <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${a.accent}, transparent 70%)` }} />
    <span aria-hidden="true" className="absolute -top-3 -right-1 font-bebas text-[7.5rem] leading-none text-white/[0.04] select-none pointer-events-none">
      {a.num}
    </span>
    <span aria-hidden="true" className="act-corner act-corner-tl" />
    <span aria-hidden="true" className="act-corner act-corner-br" />

    <div className="flex items-start justify-between relative">
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center border"
        style={{ borderColor: `${a.accent}55`, background: `${a.accent}14`, boxShadow: `0 0 18px ${a.accent}22` }}
      >
        <a.icon size={22} style={{ color: a.accent }} />
      </div>
      <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 border border-white/10 rounded-full px-3 py-1.5 bg-white/[0.03]">
        {a.date}
      </span>
    </div>

    <p className="font-mono text-[11px] tracking-[0.35em] mt-7" style={{ color: a.accent }}>
      {a.act} <span className="text-zinc-600">/ {a.num}</span>
    </p>
    <h3 className="font-bebas text-3xl tracking-wide text-white mt-1">{a.title}</h3>
    <span className="block h-px w-full my-4" style={{ background: `linear-gradient(90deg, ${a.accent}66, transparent)` }} />
    <p className="text-zinc-300 leading-relaxed text-[15px] flex-1">{a.text}</p>

    <div className="mt-7 pt-5 border-t border-white/[0.07] flex items-end justify-between gap-3">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500">{a.statLabel}</p>
        <p className="font-bebas text-4xl leading-none mt-1" style={{ color: a.accent, textShadow: `0 0 20px ${a.accent}55` }}>
          {a.statValue}
        </p>
      </div>
      <p className="font-marker text-sm -rotate-2 text-zinc-400">{a.stamp}</p>
    </div>
  </motion.article>
);

export const Lore = () => (
  <section id="legend" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="mb-14"
    >
      <p className="font-bebas tracking-[0.35em] text-[#9945FF] text-lg">THE LORE</p>
      <h2 data-testid="lore-heading" className="font-marker text-4xl sm:text-5xl lg:text-6xl gradient-sol text-glow-green mt-2 -rotate-1 inline-block">
        THE LEGEND OF CURB
      </h2>
    </motion.div>

    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
      {acts.map((a, i) => (
        <ActCard key={a.act} a={a} i={i} />
      ))}
    </div>

    <motion.blockquote
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.2 }}
      data-testid="lore-quote"
      className="mt-16 text-center"
    >
      <p className="font-marker text-3xl sm:text-4xl lg:text-5xl leading-snug">
        <span className="text-zinc-400">"That's not a lucky guess.</span>{" "}
        <span className="gradient-sol text-glow-green">That's Curb."</span>
      </p>
    </motion.blockquote>
  </section>
);
